import * as Oauth from '@dotcom/oauthjs';

import logger from './logger';
import getConfig from '../config';
import mtmClient from '../fetch/apiClient';
import { CustomerAccountAuthenticationAPI } from '../api/apis';
import {
  getAuthGuidFromAccountContextCookie,
  isUserAuthenticated,
} from './cookies';

// Helper to merge in clientId and authorizeUrl to any other options we need for oauthjs.
const makeOauthOptions = (opts = {}) => {
  const {
    oauthOptions: { clientId, authorizeUrl },
  } = getConfig();

  return {
    clientId,
    authorizeUrl,
    ...opts,
  };
};

const OAUTH_LOGIN_REDIRECT = { forceRedirect: true };

// Wrapper around Oauth.login for ease of use.
// Merges required options into the hash passed to Oauth.login
// and adds some error messaging and logging.
const login = async (opts = {}) => {
  const loginOpts = makeOauthOptions(opts);

  try {
    return await Oauth.login(loginOpts);
  } catch (error) {
    // When Oauth.login rejects due to a redirect
    // it does not reject with an error or object,
    // but with undefined instead.
    // Perhaps this is an oversight/accident here:
    //    https://github.comcast.com/xfinity-web/oauthjs/blob/v2.3.0/src/index.js#L156
    // Fix by returning an OAUTH_LOGIN_REDIRECT object.
    if (typeof error === 'undefined') {
      logger.log('Oauth.login redirect', {
        loginOpts,
        error: OAUTH_LOGIN_REDIRECT,
      });
      return OAUTH_LOGIN_REDIRECT;
    }

    logger.log('Oauth.login failed', { loginOpts, error });

    throw error;
  }
};

const forceRedirect = async (opts = {}, url = null) => {
  const logoutOpts = makeOauthOptions({
    ...opts,
    // Set `redirect: false` so that we don't destroy the isAuth cookie
    // and so that Oauth doesn't try to redirect, we will handle that ourselves.
    redirect: false,
  });
  const redirectUrl = url || Oauth.getLoginUrl(logoutOpts);

  // Oauth.logout() clears the auth info that Oauth has set in local storage
  logger.log('Oauth.logout force redirect', { logoutOpts, redirectUrl });

  Oauth.logout(logoutOpts);
  window.location.assign(redirectUrl);

  return OAUTH_LOGIN_REDIRECT;
};

// When the Oauth prompt param is multi-valued the values are separated by a space, not a comma.
const SELECT_ACCOUNT_PROMPT = 'select_billing_account';
const PASSIVE_AUTH_SELECT_ACCOUNT_PROMPT = `none ${SELECT_ACCOUNT_PROMPT}`;
const FORCE_AUTH_SELECT_ACCOUNT_PROMPT = `login ${SELECT_ACCOUNT_PROMPT}`;

/**
 * Attempts silent authentication using an iframe (no user interaction if already logged in).
 * Falls back to force authentication if user is not logged into CIMA.
 * @param {string} redirectUri - URI to redirect to if force auth is needed
 * @returns {Promise<Object>} Auth object containing accessToken, or redirects if login required
 * @throws {Error} If authentication fails for reasons other than missing login
 */
const passiveAuth = async redirectUri => {
  const {
    oauthOptions: { iframeRedirectUri },
  } = getConfig();

  try {
    return await login({
      redirect: false,
      useIframe: true,
      iframeRedirectUri,
      allowIframeLoginWithoutPromptNone: true,
      authOpts: {
        prompt: PASSIVE_AUTH_SELECT_ACCOUNT_PROMPT,
      },
    });
  } catch (err) {
    if (err.error === 'login required') {
      // The passive auth attempt failed because the customer isn't logged in to CIMA
      // Do a force auth to ask the customer to enter their credentials.
      return forceAuth(redirectUri);
    }

    throw err;
  }
};

/**
 * Wrapper around `forceRedirect()` that initiates a full login flow with account selection.
 * Redirects the user to enter credentials.
 * @param {string} redirectUri - URI to redirect to after successful authentication
 * @returns {Promise<Object>} OAUTH_LOGIN_REDIRECT object (actual redirect happens in forceRedirect)
 */
const forceAuth = redirectUri => {
  return forceRedirect({
    authOpts: {
      prompt: FORCE_AUTH_SELECT_ACCOUNT_PROMPT,
      redirect_uri: redirectUri,
    },
  });
};

// In-memory cache for APT token to avoid redundant calls to cust-auth/account.
// Uses promise caching to deduplicate concurrent in-flight requests.
// Clears automatically on page unload — no persistence across sessions.
let aptTokenCache = null; // { authGuid, cimaToken, promise }

/**
 * Clears the cached APT token.
 * @visibleForTesting
 */
export const clearAptTokenCache = () => {
  aptTokenCache = null;
};

/**
 * Exchanges a CIMA token for an account-scoped (APT) token.
 * Enables per-account authorization instead of user-level authorization.
 * Caches the result in memory so concurrent/subsequent calls for the same
 * account reuse a single API request.
 * @param {string} authGuid - Account identifier from cookie (GUID format)
 * @param {string} cimaToken - User's CIMA authentication token
 * @returns {Promise<string>} APT-based access token, or CIMA token if exchange fails or is unavailable
 */
const getAptBasedAccessToken = (authGuid, cimaToken) => {
  // If there's already a cached or in-flight request for the same account and CIMA token, reuse it
  if (
    aptTokenCache &&
    aptTokenCache.authGuid === authGuid &&
    aptTokenCache.cimaToken === cimaToken
  ) {
    return aptTokenCache.promise;
  }

  const promise = (async () => {
    const clientId = getConfig().oauthOptions?.clientId;

    try {
      const response = await mtmClient.fetch(CustomerAccountAuthenticationAPI, {
        method: 'POST',
        sessionRequired: false,
        headers: {
          'X-Auth-Guid': authGuid,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: clientId,
          access_token: cimaToken,
        }),
      });

      // API returns account-specific token on success; gracefully degrade to CIMA token otherwise
      if (response && response.access_token) {
        return response.access_token;
      }

      // Clear cache so a subsequent call can re-attempt the exchange if the missing field was transient
      aptTokenCache = null;

      logger.log(
        'APT-based access token response missing account-specific access_token',
        {
          account_guid: authGuid,
          fetchUrl: CustomerAccountAuthenticationAPI,
        },
      );

      return cimaToken;
    } catch (error) {
      // Clear cache on failure so next call retries
      aptTokenCache = null;

      // Silently fall back to CIMA token if exchange fails (API down, etc.)
      logger.log(
        'Error while attempting APT-based token exchange, falling back to CIMA token',
        {
          account_guid: authGuid,
          fetchUrl: CustomerAccountAuthenticationAPI,
          error: error && error.message ? error.message : error,
        },
      );

      return cimaToken;
    }
  })();

  aptTokenCache = { authGuid, cimaToken, promise };

  return promise;
};

export const idmLogin = redirectTo => {
  const {
    oauthOptions: { idmClientId },
    apis: { ssm },
  } = getConfig();

  return forceRedirect(
    {
      clientId: idmClientId,
    },
    `${ssm}/account-selector?continue=${window.location.origin}/support/account-management${redirectTo}&p=rsn-courtesy`,
  );
};

const { oauthOptions: { defaultRedirectUri } = {} } = getConfig();

/**
 * Retrieves an access token for API calls, optionally exchanging a CIMA token for an APT-based token.
 * @param {string} [redirectUri=defaultRedirectUri] - URI to redirect to if authentication is required
 * @returns {Promise<Object>} Auth object containing accessToken and other auth data
 * @throws {Error} If authentication fails and cannot be recovered
 */
export const getOauth = async (redirectUri = defaultRedirectUri) => {
  // If the user is unauthed and has no authentication cookies yet, there's no point trying to exchange
  // a CIMA token for an APT token in the proceeding code. So early-exit to passive auth flow
  if (!isUserAuthenticated()) {
    return passiveAuth(redirectUri);
  }

  const cimaAuth = await passiveAuth(redirectUri);
  const cimaToken = cimaAuth.accessToken;

  // Check if we should exchange CIMA token for account-specific (APT) token
  // based on feature flag and presence of auth GUID cookie
  const isAptBasedTokenEnabled = getConfig().features
    ?.showUserAccountContextUsingAPTCookie;
  const authGuid = getAuthGuidFromAccountContextCookie();

  // Use CIMA token if APT is disabled, we don't have a GUID, or token retrieval fails
  if (!isAptBasedTokenEnabled || !authGuid || !cimaToken) {
    return cimaAuth;
  }

  // Attempt to get account-scoped token; fall back to CIMA token if it fails
  const accountToken = await getAptBasedAccessToken(authGuid, cimaToken);

  if (accountToken) {
    return {
      ...cimaAuth,
      accessToken: accountToken,
    };
  }
  return cimaAuth;
};

Oauth.onAuthRevoke(() => passiveAuth(defaultRedirectUri));

export const getIdmAuth = async redirectUrl => {
  const {
    oauthOptions: { idmClientId },
  } = getConfig();

  const defaultConfig = {
    redirect: false,
    useIframe: false,
    clientId: idmClientId,
  };

  try {
    const loginOpts = makeOauthOptions(defaultConfig);
    const authInfo = await Oauth.login(loginOpts);
    if (authInfo.accessToken) {
      return authInfo;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(err);
  }
  return idmLogin(redirectUrl);
};
